import { useContext, useMemo } from "react";
import styled from "styled-components";
import ClearAll from "./ClearAll";
import { PaginationContext } from "../../../components/shared/pagination/PaginationContext";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import { useSelector } from "react-redux";
import { OwcIcon, OwcIconButton, OwcTooltip, OwcBadge, OwcTypography, OwcButton } from "@one/react";
import { SHOW_ADVANCE_FILTER_TOOLTIP } from "../../../constants";
import { SigneeFilter } from "../../../components/SigneeFilter";
import { formatUserDisplay, USER_FORMAT_PRESETS } from "../../../utils/helpers/text/userFormatting";
import Search from "./Search";
import moment from "moment-timezone";
import { useHistory } from "react-router-dom";
import { handleOnCellClick } from "../../booking-calendars/helpers";
import styles from "./BookingFilter.module.scss";


// setup Mobiscroll Moment plugin
momentTimezone.moment = moment;
const BookingFilterStyled = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 15px 10px 10px 10px;
`;

const BookingFilter = ({
  setResetAll,
  showAdvanceFilter,
  setShowAdvanceFilter,
  selectedDateRange,
  setSelectedDateRange,
  searchQuery,
  setSearchQuery,
  bookingIdFilter,
  setBookingIdFilter,
  setTempRowData,
  disableResetAll,
  setDisableResetAll,
  onSearch,
  signeeFilter,
  setSigneeFilter,
}) => {
  const user = useSelector((state) => state.user);
  const allUsers = useSelector((state) => state.users.users);
  const signeeOptions = useMemo(() => {
    const options = allUsers.map((u) => ({
      name: u.userId,
      label: formatUserDisplay(u, USER_FORMAT_PRESETS.default),
    }));
    options.sort((a, b) => a.label.localeCompare(b.label));
    return [{ name: "", label: "All users" }, ...options];
  }, [allUsers]);
  const { fetching, dispatchAction } = useContext(PaginationContext);
  const dataTimeZone = "utc";
  const displayTimeZone = user.timezone;
  const history = useHistory();

  const handleSigneeFilterChange = (selected) => {
    if (setSigneeFilter) {
      setSigneeFilter(selected);
      setDisableResetAll(false);
    }
  };

  const handleDatePickerClose = (param) => {
    if (param?.value) {
      dispatchAction({ type: "clearAll" });
      setTempRowData([]);
      setDisableResetAll(false);
      setSelectedDateRange(param?.value);
    }
  };

  const handleResetAll = () => {
    setTempRowData([]);
    setResetAll((prev) => !prev);
    setDisableResetAll(true);
    if (setSigneeFilter) {
      setSigneeFilter("");
    }
    if (bookingIdFilter) {
      setBookingIdFilter(null);
      history.push('/admin');
    } else {
      setBookingIdFilter(null);
    }
    dispatchAction({ type: "clearAll" });
  };

  const isResetDisabled = disableResetAll && searchQuery === "" && !bookingIdFilter && !signeeFilter?.name;

  return (
    <>
      <div style={{ padding: 16 }}>
        <OwcTypography variant="button" className={styles.pageTitle}>Booking Managment</OwcTypography>
      </div>
      <BookingFilterStyled data-testid="booking-filter-admin">
        <Search
          onChange={(e) => setSearchQuery(e.detail)}
          onClear={() => setSearchQuery("")}
          label="Search all columns"
          placeholder="Search all columns"
          testid={"common-search-drop-booking"}
          fetching={fetching}
          compact={false}
          searchQuery={searchQuery}
        />

        <>
          <OwcTooltip anchor={"advance-filter"} placement="top">
            {bookingIdFilter ? `Filtered by booking ID: ${bookingIdFilter}` : SHOW_ADVANCE_FILTER_TOOLTIP}
          </OwcTooltip>
          <div style={{ position: 'relative', display: 'inline-block' }} id="filter-button-wrapper">
            <OwcIconButton
              id="advance-filter"
              data-testid="advance-filter-button"
              style={{ marginRight: 8 }}
              icon=" parameter_set"
              type="filled"
              onClick={() => {
                setShowAdvanceFilter((prev) => !prev);
              }}
              flat
            />
            {bookingIdFilter && (
              <OwcBadge
                style={{
                  width: "24px",
                  height: "24px",
                  padding: "0px",
                  position: "absolute",
                  top: "-10px",
                  left: "30px",
                  pointerEvents: "none",
                }}
                type={"active"}
                title={`Filtered by booking ID: ${bookingIdFilter}`}
              >
                1
              </OwcBadge>
            )}
          </div>
        </>

        {showAdvanceFilter && (
          <>
            <div style={{ padding: "0 5px 0 5px" }} className="date-picker-my-booking">
              <Datepicker
                id="datepicker-admin-panel"
                controls={["calendar"]}
                value={selectedDateRange}
                inputStyle="box"
                style={{ height: "5px" }}
                select="range"
                display="anchored"
                disabled={!!bookingIdFilter}
                inRangeInvalid={true}
                rangeEndInvalid={true}
                startIcon={<OwcIcon name="date" type="outlined" />}
                onTempChange={handleOnCellClick}
                headerText="Experiment date(range)"
                label={bookingIdFilter ? "Date filter disabled - Reset to enable" : "Date range"}
                touchUi={"auto"}
                dateFormat="D-MMM-YYYY"
                onClose={handleDatePickerClose}
                timezonePlugin={momentTimezone}
                dataTimezone={dataTimeZone}
                displayTimezone={displayTimeZone}
                buttons={["cancel", "set"]}
                compact
                exclusiveEndDates={false}
              />
            </div>

            <SigneeFilter
              signeeFilter={signeeFilter}
              signeeOptions={signeeOptions}
              onSigneeFilterChange={handleSigneeFilterChange}
              width={200}
            />
          </>
        )}

        {onSearch && (
          <OwcButton
            variant="primary"
            style={{ height: 40 }}
            onClick={onSearch}
            disabled={!!bookingIdFilter}
          >
            Search
          </OwcButton>
        )}

        <ClearAll
          onClickCallback={handleResetAll}
          disabled={isResetDisabled}
        />
      </BookingFilterStyled>

    </>
  );
};

export default BookingFilter;
